import React, { useMemo } from 'react';
import {
  Box, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { Check } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

const BAR_COUNT = 5;
const HEARD_THRESHOLD = 0.02;

export type AudioLevelMeterProps = {
  /** Normalized mic level 0–1. */
  level: number;
};

/**
 * Live audio-level "wiggle" meter for the Camera & mic pre-flight step (R9).
 * Visibly reacts to input and confirms when the mic is working.
 */
export function AudioLevelMeter({ level }: AudioLevelMeterProps) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(1, level));
  const heard = clamped >= HEARD_THRESHOLD;

  const barHeights = useMemo(() => {
    const base = clamped * 100;
    return Array.from({ length: BAR_COUNT }, (_, index) => {
      const wave = 0.55 + 0.45 * Math.sin((index + 1) * 1.4 + clamped * 6);
      return Math.max(12, Math.round(base * wave));
    });
  }, [clamped]);

  return (
    <VStack
      space="sm"
      alignItems="center"
      width="100%"
      testID="oral-exam-audio-meter"
    >
      <HStack space="xs" alignItems="flex-end" height={48}>
        {barHeights.map((height, index) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            width={8}
            height={height}
            borderRadius="$sm"
            bg={heard ? '$brandBase' : '$borderMedium'}
            testID={`oral-exam-audio-meter-bar-${index}`}
          />
        ))}
      </HStack>

      {heard ? (
        <HStack
          space="xs"
          alignItems="center"
          testID="oral-exam-audio-meter-confirmed"
        >
          <Icon as={Check} size="sm" color="$statusSuccess" />
          <Text size="sm" color="$statusSuccess" fontWeight="semibold">
            {t('oralExam.lobby.device.audioConfirmed')}
          </Text>
        </HStack>
      ) : (
        <Text size="sm" color="$textSubtle" testID="oral-exam-audio-meter-prompt">
          {t('oralExam.lobby.device.audioPrompt')}
        </Text>
      )}
    </VStack>
  );
}
